import { useTranslation } from "react-i18next";
import { useMetrics } from "@/hooks/useMetrics";
import { useAdoption } from "@/hooks/useAdoption";
import { useProjects } from "@/hooks/useProjects";
import { useOrgGovernancePulse } from "@/hooks/useOrgGovernancePulse";
import { useTokenCost } from "@/hooks/useTokenCost";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useGateDefects, type GateDefectsState } from "@/hooks/useGateDefects";
import { useCostPerItem, type CostPerItemState } from "@/hooks/useCostPerItem";
import { usePortfolio, type PortfolioState } from "@/hooks/usePortfolio";
import {
  adaptAdoption,
  toProductivityFamily,
  toGovernanceFamily,
  toCostFamily,
} from "@/lib/executiveAdapters";
import type { ExecKpiFamily, OrgGovernanceRollup } from "@/types/executive";
import type { TokenCostItem } from "@/hooks/useTokenCost";
import type { MetricsData } from "@/hooks/useMetrics";

const TODAY_ISO = new Date().toISOString().slice(0, 10);

export interface ExecutiveDashboardState {
  isAdmin: boolean;
  loading: boolean;
  families: ExecKpiFamily[];
  costFamily: ExecKpiFamily;
  costItems: TokenCostItem[];
  rollup: OrgGovernanceRollup;
  metrics: MetricsData | null;
  gateDefects: GateDefectsState;
  costPerItem: CostPerItemState;
  portfolioState: PortfolioState;
}

export function useExecutiveDashboard(): ExecutiveDashboardState {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { metrics, loading: metricsLoading } = useMetrics();
  const { data: adoptionData, loading: adoptionLoading } = useAdoption();
  const { projects } = useProjects();
  const slugs = projects.map((p) => p.slug);
  const { rollup, loading: govLoading } = useOrgGovernancePulse(slugs);
  const { items: costItems, loading: costLoading } = useTokenCost(
    null,
    "date",
    {},
    { enabled: isAdmin },
  );

  // toCostFamily needs model-grouped items (blended rates) AND date-grouped
  // items (daily series); the API returns each grouping with the other
  // dimension null, so both queries are required.
  const { items: modelCostItems, loading: modelCostLoading } = useTokenCost(
    null,
    "model",
    {},
    { enabled: isAdmin },
  );

  const gateDefects = useGateDefects();
  const costPerItem = useCostPerItem(isAdmin);
  const portfolioState = usePortfolio();

  const loading =
    metricsLoading ||
    adoptionLoading ||
    govLoading ||
    (isAdmin && (costLoading || modelCostLoading)) ||
    gateDefects.loading ||
    costPerItem.loading ||
    portfolioState.loading;

  const families: ExecKpiFamily[] = [
    toProductivityFamily(metrics, t),
    adaptAdoption(adoptionData, t),
    toGovernanceFamily(rollup, t),
  ];

  const costFamily = toCostFamily([...costItems, ...modelCostItems], isAdmin, TODAY_ISO, t);

  return {
    isAdmin,
    loading,
    families,
    costFamily,
    costItems,
    rollup,
    metrics,
    gateDefects,
    costPerItem,
    portfolioState,
  };
}
